import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'

import { isDemoTournament, getDemoTeams } from '../data/demoData'

export const useTeams = (tournamentId) => {
  const { activeTenant } = useTenant()
  const [teams, setTeams] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (isDemoTournament(tournamentId)) {
      setTeams(getDemoTeams(tournamentId))
      setLoading(false)
      return
    }

    if (tournamentId && activeTenant) {
      fetchTeams()

      // Realtime Subscription for teams
      const channel = supabase
        .channel(`teams_live_${tournamentId}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'teams',
          filter: `tournament_id=eq.${tournamentId}`
        }, () => {
          console.log('⚡ Realtime Team Update Detected! Refetching...')
          fetchTeams()
        })
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
  }, [tournamentId, activeTenant])

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('tournament_id', tournamentId)
        .order('name')

      if (error) throw error
      setTeams(data || [])
    } catch (err) {
      console.error('Error fetching teams:', err)
    } finally {
      setLoading(false)
    }
  }

  const createTeam = async (teamData) => {
    const { data, error } = await supabase
      .from('teams')
      .insert([{
        ...teamData,
        tournament_id: tournamentId,
        tenant_id: activeTenant.id
      }])
      .select()
      .single()

    if (error) throw error
    setTeams([...teams, data])
    return data
  }

  const deleteTeam = async (teamId) => {
    const { error } = await supabase
      .from('teams')
      .delete()
      .eq('id', teamId)

    if (error) throw error
    setTeams(teams.filter(t => t.id !== teamId))
  }

  const updateTeam = async (teamId, updates) => {
    const { data, error } = await supabase
      .from('teams')
      .update(updates)
      .eq('id', teamId)
      .select()
      .single()

    if (error) throw error
    setTeams(teams.map(t => t.id === teamId ? data : t))
    return data
  }

  return { teams, setTeams, loading, createTeam, updateTeam, deleteTeam, refetch: fetchTeams }
}
